/**
 * 🪪 Ausgabe-Erkennung — identifiziert die abholende Person/Sendung aus einem Foto.
 *
 * Funktioniert für drei Eingaben: DHL-Abholbenachrichtigung, Ausweis/Führerschein
 * oder Paketlabel. Nutzt einen allgemeinen Gemini-Prompt (über /vision/analyze),
 * der Name + ALLE Sendungsnummern (auch seitlich/quer gedruckt) + Adresse liefert.
 *
 * Außerdem: Wiederverwendung des bestehenden DHL-Tracking-Tools (/track), um bei
 * „kein Paket bei uns gefunden" zu prüfen, ob die Sendung schon zugestellt wurde.
 */
import { analyzeImageWithPrompt } from './visionService';

const TRACK_URL = 'https://my-app-899771525194.europe-west1.run.app/track';

const PICKUP_PROMPT = `Dieses Foto zeigt entweder eine Paket-Abholbenachrichtigung, einen Personalausweis/Führerschein oder ein Paketlabel.
Extrahiere die abholende bzw. empfangende PERSON und ALLE sichtbaren PAKET-Sendungsnummern.
WICHTIG:
- Nachname genau erfassen: Bei einem Personalausweis/Führerschein ist der NACHNAME das Feld "Name" bzw. "Familienname" (auf dem deutschen Ausweis oben, oft in GROSSBUCHSTABEN); die "Vornamen" stehen im Feld darunter. Gib Nachname UND Vorname getrennt zurück.
- Auf dem deutschen Personalausweis stehen kleine REFERENZZEICHEN in eckigen Klammern vor den Feldern: "[a]" vor dem Namen, "[b]" vor dem Geburtsnamen usw. Diese Zeichen sind NIEMALS Teil des Namens — nicht übernehmen ("[a] Mustermann" → "Mustermann").
- Der GEBURTSNAME (Feld "[b]", oft mit "GEB." davor) ist NICHT der Nachname — den aktuellen Familiennamen aus dem Feld "Name" nehmen, den Geburtsnamen komplett ignorieren.
- "name" = "Vorname Nachname" (Vorname zuerst, Nachname zuletzt).
- Die AUSWEIS-/DOKUMENTNUMMER, Zugangsnummer, Steuer-ID, Geburtsdatum o.ä. sind KEINE Sendungsnummer — NICHT in trackingNumbers aufnehmen.
- Eine Sendungsnummer ist eine PAKET-Trackingnummer (lange Ziffern-/Buchstabenfolge, mind. 8 Zeichen), auf Abholkarten oft seitlich/quer gedruckt.
- Falls es eine ABHOLBENACHRICHTIGUNG ist: Extrahiere zusätzlich die genannte ABHOLSTELLE (Filiale/Paketshop/Abholstation, wo das Paket abzuholen ist) als "abholstelle" (Name + Straße + Ort in einem String) UND — falls eine Filial-/Shopnummer angegeben ist (z. B. "Postfiliale 553", "Filiale 558") — NUR die Nummer als "filialnummer". Ohne Angabe: null.

ZUSÄTZLICH — Lage des Dokuments im Bild (fürs automatische Zuschneiden):
- "box_2d": die achsenparallele Bounding-Box des Dokuments (Ausweis/Karte/Label), normiert auf 0–1000,
  in der Reihenfolge [ymin, xmin, ymax, xmax]. Die Box muss das GESAMTE Dokument umfassen — auch wenn
  es schräg liegt, alle vier Ecken innerhalb. Im Zweifel lieber etwas zu groß als zu klein.
  Nur angeben, wenn das Dokument klar erkennbar ist, sonst null.

Antworte AUSSCHLIESSLICH mit reinem JSON ohne weitere Erklärung:
{"name": "Vorname Nachname" oder null, "vorname": "Vorname(n)" oder null, "nachname": "Nachname" oder null, "trackingNumbers": ["..."], "strasse": "Straße" oder null, "hausnummer": "12a" oder null, "plz": "PLZ" oder null, "ort": "Ort" oder null, "abholstelle": "Name Straße Ort" oder null, "filialnummer": "553" oder null, "box_2d": [ymin, xmin, ymax, xmax] oder null}`;

function stripFences(t) {
  return String(t || '').replace(/```json/gi, '').replace(/```/g, '').trim();
}

// 🪪 Feldmarken des Personalausweises entfernen: "[a]" steht vor dem Namen, "[b]" vor dem
// Geburtsnamen — Gemini nimmt die trotz Prompt gelegentlich mit ("[a] Mustermann").
// Deterministisch wegputzen ist zuverlässiger als jede Prompt-Bitte. Auch "GEB. …"-Zusätze
// (Geburtsname) am Ende abschneiden.
function stripIdMarks(s) {
  return String(s || '')
    .replace(/\[\s*[a-z0-9]\s*\]\.?/gi, ' ')       // [a], [b], [1] …
    .replace(/\bgeb\.?\s+\S.*$/i, ' ')             // "Mustermann GEB. Schmidt" → "Mustermann"
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Liest Name + Sendungsnummern + Adresse aus einem Ausgabe-Foto.
 * @returns {{name, trackingNumbers: string[], strasse, hausnummer, plz, ort}}
 */
export async function identifyForPickup(blob) {
  const base64 = await blobToBase64(blob);
  const resp = await analyzeImageWithPrompt(base64, PICKUP_PROMPT);
  let data = {};
  try { data = JSON.parse(stripFences(resp?.text)); } catch { data = {}; }
  return {
    name: stripIdMarks(data.name),
    vorname: stripIdMarks(data.vorname),
    nachname: stripIdMarks(data.nachname),
    trackingNumbers: Array.isArray(data.trackingNumbers) ? data.trackingNumbers.filter(Boolean) : [],
    strasse: data.strasse || '',
    hausnummer: data.hausnummer || '',
    plz: data.plz || '',
    ort: data.ort || '',
    abholstelle: data.abholstelle || '',
    filialnummer: data.filialnummer ? String(data.filialnummer).replace(/\D/g, '') : '',
    // 📐 Geminis natives box_2d [ymin,xmin,ymax,xmax] — fürs Zuschneiden (siehe services/rectify.js)
    box2d: (Array.isArray(data.box_2d) && data.box_2d.length === 4) ? data.box_2d : null,
  };
}

/**
 * Fragt das bestehende DHL-Tracking-Tool ab (für „nicht bei uns gefunden").
 * @returns {object|null} trackingInfo oder null
 */
export async function trackPackage(trackingNumber) {
  try {
    const res = await fetch(TRACK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ trackingNumber }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.trackingInfo || null;
  } catch {
    return null;
  }
}
